import { create } from 'zustand';
import * as fabric from 'fabric';
import { restoreCustomProps, stripLayoutManagers } from '../utils/groupUtils';

import { CANVAS_CUSTOM_PROPS } from '../constants/editorConstants';

const MAX_HISTORY = 50;

/**
 * overflow 모드 여부에 따라 캔버스 배경색 결정
 * - overflow ON (clipOverflow=false): 'transparent' (before:render에서 직접 그림)
 * - overflow OFF (clipOverflow=true): 실제 배경색
 */
const resolveBackgroundColor = (clipOverflow, color = '#ffffff') =>
  clipOverflow ? color : 'transparent';

/**
 * 캔버스 상태를 JSON으로 직렬화
 * - overflow 모드의 'transparent' 배경 → 실제 배경색으로 교체
 * - layoutManager 필드 제거
 */
const serializeCanvas = (canvas, backgroundColor) => {
  const json = canvas.toObject(CANVAS_CUSTOM_PROPS);
  json.backgroundColor = (canvas.backgroundColor && canvas.backgroundColor !== 'transparent')
    ? canvas.backgroundColor : (backgroundColor || '#ffffff');
  return stripLayoutManagers(json);
};

/**
 * JSON 상태를 캔버스에 로드 (undo/redo/슬라이드 전환 공통)
 */
const loadCanvasState = async (canvas, state, set, get) => {
  canvas.discardActiveObject();
  set({ selectedObjects: [], isCanvasLoading: true });
  await canvas.loadFromJSON(state);
  restoreCustomProps(canvas, state);
  set({ isCanvasLoading: false });
  canvas.backgroundColor = resolveBackgroundColor(get().clipOverflow, state.backgroundColor || '#ffffff');
  canvas.requestRenderAll();
  get().triggerCenter();
};

const useEditorStore = create((set, get) => ({
  // 프로젝트 정보
  projectId: null,       // Firestore 프로젝트 ID (null = 새 프로젝트)
  projectName: '새 프로젝트',
  setProjectId: (id) => set({ projectId: id }),
  setProjectName: (name) => set({ projectName: name }),

  // 캔버스 설정
  canvasSize: { width: 1080, height: 1080 },
  backgroundColor: '#ffffff',
  zoom: 1,
  snapThreshold: 15,
  guideThickness: 3,
  clipOverflow: false, // true: 캔버스 밖 오브젝트 숨김, false: 전체 보임
  setClipOverflow: (value) => set({ clipOverflow: value }),
  isCanvasLoading: false, // loadFromJSON 중 true (overflow bounds 재계산 방지)
  setIsCanvasLoading: (value) => set({ isCanvasLoading: value }),
  overflowMargin: 100, // overflow 모드에서 오브젝트 바깥 여유 마진
  setOverflowMargin: (value) => set({ overflowMargin: value }),
  centerTrigger: 0, // 증가 시 EditorCanvas에서 스크롤 센터링 강제
  triggerCenter: () => set(s => ({ centerTrigger: s.centerTrigger + 1 })),
  customFonts: [], // fontLoader에서 로드한 커스텀 폰트 목록
  setCustomFonts: (fonts) => set({ customFonts: fonts }),
  pan: { x: 0, y: 0 },

  // 슬라이드 관리
  currentSlideIndex: 0,
  slidesData: [],  // 각 슬라이드의 캔버스 상태 저장
  docId: null,     // 현재 문서 ID (파서에서 로드한 원본)

  // 컨테이너 크기
  containerSize: { width: 0, height: 0 },
  setContainerSize: (size) => set({ containerSize: size }),

  // 캔버스 인스턴스 (Fabric.js)
  canvas: null,
  setCanvas: (canvas) => set({ canvas }),

  // 활성 도구
  activeTool: 'select', // 'select' | 'line' | 'eraser' | 'text' | 'shape' | 'image'
  setActiveTool: (tool) => {
    const { canvas } = get();
    if (canvas) {
      canvas.isDrawingMode = false;
      if (tool === 'select') {
        canvas.selection = true;
      }
    }
    set({ activeTool: tool });
  },

  // 라인 설정
  lineSettings: {
    color: '#000000',
    width: 3,
    opacity: 1,
    strokeDashArray: null, // null = 실선, [10,5] = 점선
    marker: 'none',        // 'none' | 'arrow' | 'circle' | 'diamond' | 'square' (양쪽 끝 동일)
  },
  setLineSettings: (settings) => set((state) => ({
    lineSettings: { ...state.lineSettings, ...settings }
  })),

  // 도형 설정
  shapeSettings: {
    type: 'rect', // 'rect' | 'circle' | 'triangle' | 'star' | 'polygon'
    fill: '#3b82f6',
    stroke: '#1e40af',
    strokeWidth: 2,
    strokeDashArray: null,
    opacity: 1,
    rx: 0, // 모서리 둥글기
    ry: 0,
  },
  setShapeSettings: (settings) => set((state) => ({
    shapeSettings: { ...state.shapeSettings, ...settings }
  })),

  // 텍스트 설정
  textSettings: {
    fontFamily: 'Noto Sans KR',
    fontSize: 24,
    fontColor: '#000000',
    bold: false,
    italic: false,
    underline: false,
    align: 'left',
    lineHeight: 1.3,
    charSpacing: 0,
    wordWrap: false,
    // Shadow
    shadowEnabled: false,
    shadowColor: '#000000',
    shadowOpacity: 50,
    shadowAngle: 45,
    shadowDist: 3,
    shadowBlur: 4,
    // Outline
    outlineEnabled: false,
    outlineColor: '#000000',
    outlineOpacity: 100,
    outlineWidth: 1,
    // Background (TextBoxGroup)
    bgEnabled: false,
    bgColor: '#dbeafe',
    bgAlpha: 100,
    // Border
    borderEnabled: false,
    borderColor: '#000000',
    borderOpacity: 100,
    borderWidth: 1,
  },
  setTextSettings: (settings) => set((state) => ({
    textSettings: { ...state.textSettings, ...settings }
  })),

  // 그룹 편집 모드
  editingGroupId: null,
  setEditingGroupId: (id) => set({ editingGroupId: id }),

  // 테이블 다중 셀 선택
  selectedTableCells: [],   // [{row, col}, ...]
  selectedTableGroup: null, // 현재 편집 중인 테이블 그룹 참조
  setSelectedTableCells: (cells) => set({ selectedTableCells: cells }),
  setSelectedTableGroup: (group) => set({ selectedTableGroup: group }),
  clearTableCellSelection: () => set({ selectedTableCells: [], selectedTableGroup: null }),

  // 그룹화
  groupObjects: () => {
    const { canvas, pushHistory, syncLayersFromCanvas } = get();
    if (!canvas) return;

    const activeObject = canvas.getActiveObject();
    if (!activeObject || activeObject.type !== 'activeselection') return;

    const objects = activeObject.getObjects();
    if (objects.length < 2) return;

    // ActiveSelection 해제
    canvas.discardActiveObject();

    // 선택된 객체들을 캔버스에서 제거
    objects.forEach(obj => canvas.remove(obj));

    // 새 그룹 생성
    const group = new fabric.Group(objects, {
      id: `group-${Date.now()}`,
      name: `Group`,
      subTargetCheck: false,
      interactive: false,
      _layerExpanded: true,
    });

    canvas.add(group);
    canvas.setActiveObject(group);
    canvas.requestRenderAll();
    syncLayersFromCanvas();
    pushHistory();
  },

  // 그룹 해제
  ungroupObjects: () => {
    const { canvas, pushHistory, syncLayersFromCanvas } = get();
    if (!canvas) return;

    const activeObject = canvas.getActiveObject();
    if (!activeObject || activeObject.type !== 'group') return;
    if (activeObject._isTextBoxGroup) return;  // TextBoxGroup 해제 방지
    if (activeObject._isTableGroup) return;  // TableGroup 해제 방지

    const items = activeObject.getObjects();
    // removeAll()로 자식들의 좌표를 월드 좌표로 변환
    activeObject.removeAll();
    canvas.remove(activeObject);

    // 개별 객체를 캔버스에 추가
    items.forEach(obj => {
      canvas.add(obj);
    });

    // ActiveSelection으로 재선택
    if (items.length > 0) {
      const sel = new fabric.ActiveSelection(items, { canvas });
      canvas.setActiveObject(sel);
    }

    canvas.requestRenderAll();
    syncLayersFromCanvas();
    pushHistory();
  },

  // 그룹 편집 모드 종료 (모든 중첩 그룹의 interactive 해제)
  exitGroupEditing: () => {
    const { canvas, editingGroupId } = get();
    if (!canvas || !editingGroupId) return;
    const wasTableEditing = canvas.getObjects().some(o => o.id === editingGroupId && o._isTableGroup);

    // 모든 그룹의 interactive/subTargetCheck 재귀적으로 해제
    const resetGroups = (objects) => {
      for (const obj of objects) {
        if (obj.type === 'group') {
          // TableGroup은 subTargetCheck 유지 (셀 클릭 감지용)
          if (obj._isTableGroup) {
            const savedLeft = obj.left;
            const savedTop = obj.top;

            obj.set({ interactive: false, subTargetCheck: false, hasBorders: true, hasControls: true, lockMovementX: false, lockMovementY: false });

            (obj.getObjects ? obj.getObjects() : []).forEach(child => {
              // 셀 하이라이트 제거 (stroke: null 복원, _origStroke/_origStrokeWidth는 영구 메타데이터이므로 유지)
              if (child._isTableCell) {
                child.set({ stroke: null, strokeWidth: 0 });
                delete child._hlActive;
                if (child._origFill !== undefined) {
                  child.set({ fill: child._origFill });
                  delete child._origFill;
                }
              }
              // 셀 텍스트 선택 컨트롤 복원 + 이동 잠금 해제
              if (child._isTableCellText) {
                child.set({ hasBorders: true, hasControls: true, lockMovementX: false, lockMovementY: false });
              }
            });

            // 위치 복원 (interactive:false 전환이 위치를 바꿀 경우 대비)
            obj.set({ left: savedLeft, top: savedTop });
            obj.setCoords();
          } else {
            obj.set({ interactive: false, subTargetCheck: false });
          }
          resetGroups(obj.getObjects());
        }
      }
    };
    resetGroups(canvas.getObjects());

    canvas.discardActiveObject();
    canvas.requestRenderAll();

    set({ editingGroupId: null, selectedTableCells: [], selectedTableGroup: null });

    // 테이블 셀 편집 종료 후 히스토리 저장 (interactive: false 상태로 저장)
    if (wasTableEditing) get().pushHistory();
  },

  // 선택된 객체
  selectedObjects: [],
  setSelectedObjects: (objects) => set({ selectedObjects: objects }),

  // 레이어
  layers: [],
  setLayers: (layers) => set({ layers }),

  // 히스토리 (언두/리두)
  history: [],
  historyIndex: -1,

  pushHistory: () => {
    const { canvas, history, historyIndex, backgroundColor } = get();
    if (!canvas) return;

    const canvasState = JSON.stringify(serializeCanvas(canvas, backgroundColor));

    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(canvasState);

    // 최대 개수 제한
    if (newHistory.length > MAX_HISTORY) {
      newHistory.shift();
    }

    set({
      history: newHistory,
      historyIndex: newHistory.length - 1,
    });
  },

  undo: async () => {
    const { canvas, history, historyIndex } = get();
    if (historyIndex <= 0 || !canvas) return;
    // 테이블/그룹 편집 상태 초기화 (캔버스 재로드 시 stale 참조 방지)
    set({ editingGroupId: null, selectedTableCells: [], selectedTableGroup: null });
    const newIndex = historyIndex - 1;
    await loadCanvasState(canvas, JSON.parse(history[newIndex]), set, get);
    get().syncLayersFromCanvas();
    set({ historyIndex: newIndex });
  },

  redo: async () => {
    const { canvas, history, historyIndex } = get();
    if (historyIndex >= history.length - 1 || !canvas) return;
    // 테이블/그룹 편집 상태 초기화 (캔버스 재로드 시 stale 참조 방지)
    set({ editingGroupId: null, selectedTableCells: [], selectedTableGroup: null });
    const newIndex = historyIndex + 1;
    await loadCanvasState(canvas, JSON.parse(history[newIndex]), set, get);
    get().syncLayersFromCanvas();
    set({ historyIndex: newIndex });
  },

  // 캔버스에서 레이어 동기화 (재귀: 다단계 그룹 지원)
  syncLayersFromCanvas: () => {
    const { canvas } = get();
    if (!canvas) return;

    const buildLayerTree = (objects) => {
      return objects.map((obj, index) => {
        // id가 없으면 생성하여 캔버스 객체에도 설정 (일관성 보장)
        if (!obj.id) {
          obj.id = `layer-${Date.now()}-${Math.random().toString(36).substr(2, 5)}-${index}`;
        }
        if (!obj.name) {
          obj.name = `Layer ${index + 1}`;
        }

        const base = {
          id: obj.id,
          name: obj.name,
          type: obj.type,
          visible: obj.visible !== false,
          locked: obj.selectable === false,
          order: index,
        };

        if (obj.type === 'group' && obj._isTextBoxGroup) {
          return { ...base, type: 'textbox' };  // 'T' 아이콘, children 없음
        }
        if (obj.type === 'group' && obj._isTableGroup) {
          return { ...base, type: 'table' };  // 단일 레이어, children 없음
        }
        if (obj.type === 'group') {
          return {
            ...base,
            expanded: obj._layerExpanded !== false,
            children: buildLayerTree(obj.getObjects()),
          };
        }
        return base;
      });
    };

    set({ layers: buildLayerTree(canvas.getObjects()) });
  },

  // 줌 (CSS transform으로 처리하므로 상태만 업데이트)
  setZoom: (zoom) => {
    const newZoom = Math.max(0.1, Math.min(2, zoom));
    set({ zoom: newZoom });
  },

  setSnapThreshold: (value) => set({ snapThreshold: value }),
  setGuideThickness: (value) => set({ guideThickness: value }),

  // 배경색
  setBackgroundColor: (color) => {
    const { canvas, clipOverflow } = get();
    if (canvas) {
      canvas.backgroundColor = resolveBackgroundColor(clipOverflow, color);
      canvas.renderAll();
    }
    set({ backgroundColor: color });
  },

  // 캔버스 크기 변경 (DOM 요소 포함)
  setCanvasSize: (size) => {
    const { canvas } = get();
    if (canvas) {
      const { width, height } = size;
      const lowerCanvas = canvas.lowerCanvasEl;
      const upperCanvas = canvas.upperCanvasEl;
      const wrapperEl = canvas.wrapperEl;

      // 1. Fabric.js 내부 크기 설정
      canvas.width = width;
      canvas.height = height;

      // 2. DOM 요소 속성 및 스타일 설정
      if (lowerCanvas) {
        lowerCanvas.width = width;
        lowerCanvas.height = height;
        lowerCanvas.style.width = width + 'px';
        lowerCanvas.style.height = height + 'px';
      }
      if (upperCanvas) {
        upperCanvas.width = width;
        upperCanvas.height = height;
        upperCanvas.style.width = width + 'px';
        upperCanvas.style.height = height + 'px';
      }
      if (wrapperEl) {
        wrapperEl.style.width = width + 'px';
        wrapperEl.style.height = height + 'px';
      }

      canvas.calcOffset();
      canvas.requestRenderAll();
    }
    set({ canvasSize: size });
  },

  // 캔버스 초기화
  clearCanvas: () => {
    const { canvas, clipOverflow } = get();
    if (canvas) {
      canvas.clear();
      canvas.backgroundColor = resolveBackgroundColor(clipOverflow);
      canvas.renderAll();
    }
    set({
      layers: [],
      history: [],
      historyIndex: -1,
      selectedObjects: [],
    });
  },

  // 슬라이드 데이터 설정
  setSlidesData: (slides, docId = null) => {
    set({ slidesData: slides, docId, currentSlideIndex: 0 });
  },

  // 현재 슬라이드 저장 (전환 전 호출)
  saveCurrentSlide: () => {
    const { canvas, slidesData, currentSlideIndex, backgroundColor } = get();
    if (!canvas) return;

    const newSlidesData = [...slidesData];
    newSlidesData[currentSlideIndex] = {
      ...newSlidesData[currentSlideIndex],
      canvasState: JSON.stringify(serializeCanvas(canvas, backgroundColor)),
    };
    set({ slidesData: newSlidesData });
  },

  // 슬라이드 전환 (convertSlide 함수를 파라미터로 받음)
  goToSlide: async (index, convertSlide = null) => {
    const { canvas, slidesData, currentSlideIndex, syncLayersFromCanvas, clipOverflow } = get();
    if (!canvas || index < 0 || index >= slidesData.length || index === currentSlideIndex) return;

    get().saveCurrentSlide();

    const targetSlide = slidesData[index];
    if (targetSlide.canvasState) {
      await loadCanvasState(canvas, JSON.parse(targetSlide.canvasState), set, get);
    } else if (targetSlide.layers && convertSlide) {
      set({ isCanvasLoading: true });
      canvas.clear();
      canvas.backgroundColor = resolveBackgroundColor(clipOverflow);
      const fabricObjects = await convertSlide(targetSlide.layers);
      for (const obj of fabricObjects) {
        canvas.add(obj);
      }
      set({ isCanvasLoading: false });
      canvas.requestRenderAll();
      get().triggerCenter();
    }
    syncLayersFromCanvas();

    set({
      currentSlideIndex: index,
      history: [],
      historyIndex: -1,
    });

    get().pushHistory();
  },

  // 새 슬라이드 추가
  addSlide: () => {
    const { canvas, slidesData, syncLayersFromCanvas, backgroundColor } = get();
    if (!canvas) return;

    let newSlidesData;

    // slidesData가 비어있으면 현재 캔버스를 첫 번째 슬라이드로 저장
    if (slidesData.length === 0) {
      const canvasJSON = canvas.toObject(CANVAS_CUSTOM_PROPS);
      canvasJSON.backgroundColor = canvas.backgroundColor || backgroundColor;

      newSlidesData = [{
        layers: [],
        canvasState: JSON.stringify(canvasJSON),
      }];
    } else {
      // 현재 슬라이드 저장
      get().saveCurrentSlide();
      newSlidesData = [...get().slidesData];
    }

    // 빈 슬라이드 추가
    const newSlide = {
      layers: [],
      canvasState: null,
    };
    newSlidesData.push(newSlide);
    const newIndex = newSlidesData.length - 1;

    // 캔버스 초기화
    canvas.clear();
    canvas.backgroundColor = resolveBackgroundColor(get().clipOverflow);
    canvas.requestRenderAll();
    syncLayersFromCanvas();

    set({
      slidesData: newSlidesData,
      currentSlideIndex: newIndex,
      history: [],
      historyIndex: -1,
    });

    // 히스토리 초기화 후 첫 상태 저장
    get().pushHistory();
  },

  // 현재 슬라이드 삭제 (2개 이상일 때만)
  deleteSlide: () => {
    const { canvas, slidesData, currentSlideIndex, syncLayersFromCanvas } = get();
    if (!canvas || slidesData.length < 2) return;

    const newSlidesData = slidesData.filter((_, idx) => idx !== currentSlideIndex);
    const newIndex = Math.min(currentSlideIndex, newSlidesData.length - 1);
    const targetSlide = newSlidesData[newIndex];

    // 새 슬라이드 로드
    if (targetSlide.canvasState) {
      const state = stripLayoutManagers(JSON.parse(targetSlide.canvasState));
      canvas.loadFromJSON(state).then(() => {
        restoreCustomProps(canvas, state);
        canvas.backgroundColor = resolveBackgroundColor(get().clipOverflow, state.backgroundColor || '#ffffff');
        canvas.requestRenderAll();
        syncLayersFromCanvas();
      });
    } else {
      canvas.clear();
      canvas.backgroundColor = resolveBackgroundColor(get().clipOverflow);
      canvas.requestRenderAll();
      syncLayersFromCanvas();
    }

    set({
      slidesData: newSlidesData,
      currentSlideIndex: newIndex,
      history: [],
      historyIndex: -1,
    });

    get().pushHistory();
  },
}));

export default useEditorStore;
